import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { UserPlus, ArrowLeft, Phone, Mail, Building2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import MonthPicker, { currentMonthYm, humanMonth } from "@/components/MonthPicker";

export default function DashboardJoiners() {
  const [params] = useSearchParams();
  const month = params.get("month") || currentMonthYm();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/dashboard/joiners?month=${month}`);
        if (live) setData(data);
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, [month]);

  return (
    <div className="space-y-5" data-testid="dashboard-joiners-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Link to="/" className="text-slate-400 hover:text-slate-700" data-testid="back-to-dashboard">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <UserPlus className="h-7 w-7 text-emerald-600" />
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400">HR Snapshot</div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
              New Joiners · {humanMonth(month)}
            </h1>
          </div>
        </div>
        <MonthPicker testId="joiners-month" />
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4 flex items-center gap-3" data-testid="joiners-count-strip">
        <div className="text-3xl font-bold text-emerald-600" style={{ fontFamily: "Chivo" }}>
          {loading ? "…" : (data?.count ?? 0)}
        </div>
        <div className="text-sm text-slate-600">
          employees joined between <span className="font-mono">{data?.month_first}</span> and{" "}
          <span className="font-mono">{data?.month_last}</span>
        </div>
      </div>

      {!loading && (data?.count ?? 0) === 0 && (
        <div className="border border-dashed border-slate-300 rounded-lg p-12 text-center text-sm text-slate-400">
          No new joiners in {humanMonth(month)}.
        </div>
      )}

      {!loading && (data?.count ?? 0) > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                <th className="px-4 py-2">Employee</th>
                <th className="px-4 py-2">Punch ID / Code</th>
                <th className="px-4 py-2">Category</th>
                <th className="px-4 py-2">Department</th>
                <th className="px-4 py-2">Unit</th>
                <th className="px-4 py-2">DOJ</th>
                <th className="px-4 py-2">CTC</th>
                <th className="px-4 py-2">Contact</th>
              </tr>
            </thead>
            <tbody>
              {(data?.rows || []).map((e) => (
                <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`joiner-row-${e.id}`}>
                  <td className="px-4 py-3">
                    <Link to={`/employees/${e.id}`} className="font-semibold text-slate-900 hover:text-red-600">
                      {e.name}
                    </Link>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-600">
                    <div>{e.punch_id}</div>
                    <div className="text-slate-400">{e.employee_code}</div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge className="bg-slate-100 text-slate-700 text-[10px]">
                      {e.category || "—"}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-slate-700 text-xs">{e.department_name || "—"}</td>
                  <td className="px-4 py-3 text-slate-700 text-xs">
                    <div className="flex items-center gap-1">
                      <Building2 className="h-3 w-3 text-slate-400" />
                      {e.unit_name || "—"}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-700">{e.doj}</td>
                  <td className="px-4 py-3 font-mono text-sm text-slate-900">
                    ₹{(e.ctc || 0).toLocaleString("en-IN")}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">
                    {e.mobile && (
                      <div className="flex items-center gap-1">
                        <Phone className="h-3 w-3 text-slate-400" />{e.mobile}
                      </div>
                    )}
                    {e.email && (
                      <div className="flex items-center gap-1 text-slate-500">
                        <Mail className="h-3 w-3 text-slate-400" />{e.email}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
